import { useLayoutEffect, useState } from "preact/hooks";
import type { MapDocument, MapLogo } from "../shared/types";
import {
  MAP_LOGO_MAX_DIMENSION,
  MAP_LOGO_MAX_PIXELS,
  validLogoId,
} from "../shared/validation";

export interface VenueMapLogoAsset {
  /** Already decoded, bounded pixels; WebGL consumes these without decoding a PNG again. */
  image: HTMLImageElement | HTMLCanvasElement;
  /** Local raster URL shared by the SVG renderer and booth previews. */
  url: string;
  width: number;
  height: number;
}

export type VenueMapLogos = ReadonlyMap<string, VenueMapLogoAsset>;
export interface VenueMapLogoLoad {
  ready: Promise<VenueMapLogos>;
  dispose(): void;
}

const EMPTY_LOGOS: VenueMapLogos = new Map();
const MAX_BYTES = 2 * 1024 * 1024;
const DECODED_PIXEL_BUDGET = 4_000_000;
const MAX_OUTPUT_DIMENSION = 512;
const LOAD_TIMEOUT = 10_000;
// SVG assets have already been normalized and validated by the logo API.
// Decode them as isolated images, then retain only bounded canvas/PNG pixels;
// uploaded SVG markup never enters the map DOM. Raster images already within
// the retained-pixel budget can keep their decoded image and original blob URL.
const MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
]);

type LogoPlan = { key: string; base: string; logos: MapLogo[] };

function validDimensions(width: number, height: number): boolean {
  return (
    Number.isInteger(width) &&
    Number.isInteger(height) &&
    width >= 1 &&
    height >= 1 &&
    width <= MAP_LOGO_MAX_DIMENSION &&
    height <= MAP_LOGO_MAX_DIMENSION &&
    width * height <= MAP_LOGO_MAX_PIXELS
  );
}

function logoBase(document: MapDocument, supplied?: string): string {
  const base =
    supplied ?? `/api/v1/maps/${encodeURIComponent(document.id)}/logos`;
  if (/^\/(?!\/)/.test(base)) {
    if (/[?#\\\s]/.test(base)) throw new Error("Invalid logo asset path");
    return base.replace(/\/+$/, "");
  }
  const url = new URL(base);
  if (
    !/^https?:$/.test(url.protocol) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash
  ) {
    throw new Error("Invalid logo asset origin");
  }
  return url.href.replace(/\/+$/, "");
}

function logoPlan(document: MapDocument, baseUrl?: string): LogoPlan {
  let base: string;
  try {
    base = logoBase(document, baseUrl);
  } catch {
    return { key: "invalid", base: "", logos: [] };
  }
  const references = new Map<string, MapLogo>();
  for (const stand of document.geometry.stands) {
    const logo = stand.logo;
    if (
      stand.hidden ||
      !logo ||
      (stand.labelMode !== "logo" && stand.labelMode !== "logo-text")
    )
      continue;
    if (!validLogoId(logo.id) || !validDimensions(logo.width, logo.height))
      continue;
    references.set(logo.id, logo);
  }
  const logos = [...references.values()].sort((left, right) =>
    left.id.localeCompare(right.id),
  );
  return {
    base,
    logos,
    key: JSON.stringify([
      base,
      logos.map(({ id, width, height }) => [id, width, height]),
    ]),
  };
}

/** Stable across camera, route appearance and other changes that do not affect logo assets. */
export function venueMapLogoKey(
  document: MapDocument,
  logoBaseUrl?: string,
): string {
  return logoPlan(document, logoBaseUrl).key;
}

function abortError(): DOMException {
  return new DOMException("Logo loading cancelled", "AbortError");
}

function releaseImage(image: HTMLImageElement): void {
  image.onload = null;
  image.onerror = null;
  image.removeAttribute("src");
}

function releaseAsset(asset: VenueMapLogoAsset): void {
  if ("naturalWidth" in asset.image) releaseImage(asset.image);
  else {
    asset.image.width = 1;
    asset.image.height = 1;
  }
  URL.revokeObjectURL(asset.url);
}

function decodeImage(
  url: string,
  signal: AbortSignal,
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const image = new Image();
    image.decoding = "async";
    let settled = false;
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", cancelled);
      image.onload = null;
      image.onerror = null;
      if (error) {
        releaseImage(image);
        reject(error);
      } else resolve(image);
    };
    const cancelled = () => finish(abortError());
    signal.addEventListener("abort", cancelled, { once: true });
    image.onerror = () => finish(new Error("Invalid logo image"));
    image.onload = () => {
      if (typeof image.decode !== "function") finish();
    };
    image.src = url;
    if (typeof image.decode === "function") {
      image.decode().then(
        () => finish(),
        (error: unknown) => finish(error),
      );
    }
  });
}

async function imageBlob(
  response: Response,
  signal: AbortSignal,
): Promise<Blob> {
  const mime = response.headers
    .get("Content-Type")
    ?.split(";", 1)[0]
    .trim()
    .toLowerCase();
  if (
    !response.ok ||
    !mime ||
    !MIME_TYPES.has(mime) ||
    !response.body ||
    Number(response.headers.get("Content-Length")) > MAX_BYTES
  ) {
    await response.body?.cancel();
    throw new Error("Invalid logo response");
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  let size = 0;
  const cancel = () => {
    void reader.cancel().catch(() => {});
  };
  signal.addEventListener("abort", cancel, { once: true });
  if (signal.aborted) cancel();
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_BYTES) {
        await reader.cancel();
        throw new Error("Logo image exceeds the size limit");
      }
      chunks.push(new Uint8Array(value));
    }
    if (signal.aborted) throw abortError();
    if (!size) throw new Error("Empty logo image");
    return new Blob(chunks, { type: mime });
  } finally {
    signal.removeEventListener("abort", cancel);
    reader.releaseLock();
  }
}

function pngBlob(
  canvas: HTMLCanvasElement,
  signal: AbortSignal,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    let settled = false;
    const cancelled = () => finish(null, abortError());
    const finish = (blob: Blob | null, error?: unknown) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", cancelled);
      if (error || !blob)
        reject(error ?? new Error("Logo image could not be resized"));
      else resolve(blob);
    };
    signal.addEventListener("abort", cancelled, { once: true });
    try {
      canvas.toBlob((blob) => finish(blob), "image/png");
    } catch (error) {
      finish(null, error);
    }
  });
}

async function loadAsset(
  reference: MapLogo,
  base: string,
  maxDimension: number,
  parentSignal: AbortSignal,
): Promise<VenueMapLogoAsset | undefined> {
  if (parentSignal.aborted) return undefined;
  const controller = new AbortController();
  const cancel = () => controller.abort();
  parentSignal.addEventListener("abort", cancel, { once: true });
  const timeout = setTimeout(cancel, LOAD_TIMEOUT);
  let sourceUrl: string | undefined;
  let sourceImage: HTMLImageElement | undefined;
  let outputUrl: string | undefined;
  let canvas: HTMLCanvasElement | undefined;
  try {
    const response = await fetch(`${base}/${reference.id}`, {
      signal: controller.signal,
      mode: "cors",
      credentials: "same-origin",
      redirect: "error",
    });
    const blob = await imageBlob(response, controller.signal);
    if (controller.signal.aborted) throw abortError();
    sourceUrl = URL.createObjectURL(blob);
    sourceImage = await decodeImage(sourceUrl, controller.signal);
    const { naturalWidth: sourceWidth, naturalHeight: sourceHeight } =
      sourceImage;
    if (
      !validDimensions(sourceWidth, sourceHeight) ||
      sourceWidth !== reference.width ||
      sourceHeight !== reference.height
    ) {
      throw new Error("Logo dimensions do not match the map");
    }
    if (
      blob.type !== "image/svg+xml" &&
      Math.max(sourceWidth, sourceHeight) <= maxDimension
    ) {
      // Immutable raster originals are already validated and bounded. Keeping
      // this decode avoids a canvas allocation, PNG encode and second decode.
      const asset = {
        image: sourceImage,
        url: sourceUrl,
        width: sourceWidth,
        height: sourceHeight,
      };
      sourceImage = undefined;
      sourceUrl = undefined;
      return asset;
    }
    const scale = Math.min(
      1,
      maxDimension / Math.max(sourceWidth, sourceHeight),
    );
    const width = Math.max(1, Math.floor(sourceWidth * scale));
    const height = Math.max(1, Math.floor(sourceHeight * scale));
    canvas = globalThis.document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Logo canvas is unavailable");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(sourceImage, 0, 0, width, height);
    releaseImage(sourceImage);
    sourceImage = undefined;
    URL.revokeObjectURL(sourceUrl);
    sourceUrl = undefined;
    const resized = await pngBlob(canvas, controller.signal);
    if (controller.signal.aborted) throw abortError();
    outputUrl = URL.createObjectURL(resized);
    // The resized canvas is already drawable. Keep it for WebGL and encode
    // only the URL needed by SVG/previews; decoding that PNG again duplicates
    // pixels and work. Ownership transfers to the completed asset.
    const asset = { image: canvas, url: outputUrl, width, height };
    canvas = undefined;
    outputUrl = undefined;
    return asset;
  } catch {
    // A missing, withdrawn, unsupported or failed logo always keeps the text label usable.
    return undefined;
  } finally {
    clearTimeout(timeout);
    parentSignal.removeEventListener("abort", cancel);
    controller.abort();
    if (sourceImage) releaseImage(sourceImage);
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    if (outputUrl) URL.revokeObjectURL(outputUrl);
    if (canvas) {
      canvas.width = 1;
      canvas.height = 1;
    }
  }
}

/** Loads one bounded batch. Disposing also cancels queued/in-flight work and releases late results. */
export function loadVenueMapLogos(
  document: MapDocument,
  logoBaseUrl?: string,
  signal?: AbortSignal,
): VenueMapLogoLoad {
  const plan = logoPlan(document, logoBaseUrl);
  const controller = new AbortController();
  const loaded = new Map<string, VenueMapLogoAsset>();
  let next = 0;
  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    controller.abort();
    signal?.removeEventListener("abort", dispose);
    for (const asset of loaded.values()) releaseAsset(asset);
    loaded.clear();
  };
  signal?.addEventListener("abort", dispose, { once: true });
  if (signal?.aborted) dispose();
  const pixelsPerLogo = DECODED_PIXEL_BUDGET / Math.max(1, plan.logos.length);
  const outputDimension = (logo: MapLogo) =>
    Math.max(
      1,
      Math.min(
        MAX_OUTPUT_DIMENSION,
        // Reserve the same bounded pixel share for every asset, accounting for
        // its actual aspect ratio instead of treating wide logos as squares.
        Math.floor(
          Math.sqrt(
            (pixelsPerLogo * Math.max(logo.width, logo.height)) /
              Math.min(logo.width, logo.height),
          ),
        ),
        // Very thin images still retain at least one pixel on the short axis.
        Math.floor(pixelsPerLogo),
      ),
    );
  const worker = async () => {
    while (!controller.signal.aborted && next < plan.logos.length) {
      const reference = plan.logos[next++];
      const asset = await loadAsset(
        reference,
        plan.base,
        outputDimension(reference),
        controller.signal,
      );
      if (!asset) continue;
      if (controller.signal.aborted) releaseAsset(asset);
      else loaded.set(reference.id, asset);
    }
  };
  const ready = Promise.all(
    Array.from({ length: Math.min(3, plan.logos.length) }, worker),
  ).then((): VenueMapLogos =>
    controller.signal.aborted || loaded.size === 0 ? EMPTY_LOGOS : loaded,
  );
  return { ready, dispose };
}

/** Publish the completed batch once so WebGL does not rebuild its atlas for each arriving image. */
export function useVenueMapLogos(
  document: MapDocument,
  logoBaseUrl?: string,
  supplied?: VenueMapLogos,
): VenueMapLogos {
  const key = supplied ? "supplied" : venueMapLogoKey(document, logoBaseUrl);
  const [state, setState] = useState<{ key: string; logos: VenueMapLogos }>({
    key: "",
    logos: EMPTY_LOGOS,
  });
  useLayoutEffect(() => {
    // A parent can share decoded assets with directory thumbnails. It owns
    // their lifetime; this instance must neither reload nor dispose them.
    if (supplied) return;
    const controller = new AbortController();
    const load = loadVenueMapLogos(document, logoBaseUrl, controller.signal);
    void load.ready.then((logos) => {
      if (!controller.signal.aborted) setState({ key, logos });
    });
    return () => {
      controller.abort();
      load.dispose();
    };
  }, [key]);
  return supplied ?? (state.key === key ? state.logos : EMPTY_LOGOS);
}
